import { apiClient } from './client';

// ── Export View State ────────────────────────────────────────────────────

// ARGOS policy: mg/L ONLY. This type is retained for API stability but
// narrowed to a single value. Do NOT re-introduce ug/L, ppb, or similar.
export type ConcentrationUnit = 'mg/L';

export interface ExportViewState {
  activeTab: string;
  viewMode: 'table' | 'chart';
  chartType?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  concUnit: ConcentrationUnit;
}

// ── Types ────────────────────────────────────────────────────────────────

export interface FilterSiteItem {
  id: string;
  name: string;
  site_identifier: string | null;
}

export interface FilterWellItem {
  id: string;
  well_name: string;
  well_type: string | null;
  site_id: string;
  site_name: string;
  // Well construction data for client-side validation
  casing_elevation_ft: number | null;
  screen_top_ft: number | null;
  screen_bottom_ft: number | null;
  total_depth_ft: number | null;
}

export interface FilterAnalyteItem {
  code: string;
  name: string | null;
  group: string | null;
}

export type NdHandling = 'zero' | 'half_mdl' | 'mdl' | 'less_than' | 'exclude';

/** One regulatory limit, with whether the row's concentration crossed it. */
export interface ExceedanceLimitDetail {
  library: string;          // "epa-mcl", "tceq-g1", "consent_order", "cleanup_goal", ...
  label: string;            // Human-readable display label
  value: number;            // mg/L (ARGOS policy)
  units: string;            // always "mg/L"
  exceeded: boolean;
  color: string | null;
}

/** Structured exceedance verdict for one analytical row. */
export interface ExceedanceDecision {
  triggered: boolean;
  rule: 'mcl' | 'site_specific' | 'strictest';
  driving_limit: ExceedanceLimitDetail | null;
  all_limits: ExceedanceLimitDetail[];
  reason: string;
}

export type ExceedanceRule = 'mcl' | 'site_specific' | 'strictest';

export interface AnalyticalRow {
  id: string;
  site_id: string;
  site_name: string;
  well_id: string | null;
  well_name: string | null;
  sample_date: string;
  compound: string;
  analyte_code: string | null;
  concentration: number | null;
  concentration_ugl: number | null;
  units: string | null;
  detection_limit: number | null;
  is_non_detect: boolean;
  /** QC rider classification: 'N' = field sample, else SUR/FD/FB/TB/LCS/MS/MSD/DUP.
   *  The QA/QC tab shows only sample_type != 'N'. */
  sample_type: string | null;
  mcl: number | null;
  /** @deprecated Use `exceedance.triggered` — kept one release for migration. */
  exceeds_mcl: boolean | null;
  /** Structured exceedance verdict respecting the per-site `exceedance_rule`.
   *  This is the canonical source of truth for grid colouring. */
  exceedance: ExceedanceDecision | null;
  qualifier: string | null;
  lab_method: string | null;
  regulatory_limits?: Record<string, RegulatoryLimitInfo>;
  /** Unit-anomaly verdict from the intake validator: NULL = clean.
   *  When set, the grid shows a ⚠ icon and the row appears in the QA queue
   *  until an operator confirms / corrects / dismisses. */
  qa_flag?: string | null;
  qa_flag_detail?: {
    expected_median?: number;
    observed?: number;
    ratio?: number;
    n_samples?: number;
    units_raw?: string;
    reason?: string;
  } | null;
  qa_review_action?: 'confirmed' | 'corrected' | 'dismissed' | null;
  /** Free-text notes from the EDD (lab/legend/well-status). Shown in the QA/QC tab. */
  notes?: string | null;
  /** QC engine metrics (QA/QC tab) — latest run per row. Null for field samples
   *  and checks that don't compute them (e.g. blanks have no recovery/RPD). */
  qc_recovery_pct?: number | null;
  qc_rpd_pct?: number | null;
  qc_passed?: boolean | null;
}

export interface AnalyticalResponse {
  items: AnalyticalRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  nd_handling: string;
}

// ── Analytical Timeseries Types ──────────────────────────────────────────

export interface AnalyticalTimeseriesMeasurement {
  date: string;
  concentration: number | null;
  concentration_ugl: number | null;
  is_non_detect: boolean;
  detection_limit: number | null;
}

export interface AnalyticalTimeseriesAnalyte {
  compound: string;
  mcl: number | null;
  measurements: AnalyticalTimeseriesMeasurement[];
  regulatory_limits?: Record<string, RegulatoryLimitInfo>;
}

export interface AnalyticalTimeseriesWell {
  well_id: string;
  well_name: string;
  site_name: string;
  latitude?: number | null;
  longitude?: number | null;
  screen_top_ft?: number | null;
  screen_bottom_ft?: number | null;
  total_depth_ft?: number | null;
  casing_elevation_ft?: number | null;
  analytes: Record<string, AnalyticalTimeseriesAnalyte>;
}

export interface AnalyticalTimeseriesResponse {
  wells: AnalyticalTimeseriesWell[];
}

// ── Phase 2: Gauging Types ───────────────────────────────────────────────

export interface GaugingRow {
  id: string;
  site_id: string;
  site_name: string;
  well_id: string | null;
  well_name: string | null;
  gauge_date: string;
  depth_to_water_ft: number | null;
  water_elevation_ft: number | null;
  casing_elevation_ft: number | null;
  corrected_psh_ft: number | null;
  depth_to_product_ft: number | null;
  product_thickness_ft: number | null;
  specific_gravity: number | null;
  /** Provenance of the SG used in the corrected_psh_ft computation:
   *  "well" = per-well override; "site" = site default; "default" = 0.800 fallback. */
  specific_gravity_source: 'well' | 'site' | 'default' | null;
  well_group: string | null;
  is_dry: boolean;
  flag_code: string | null;
  notes: string | null;
}

export interface GaugingResponse {
  items: GaugingRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface TimeseriesMeasurement {
  date: string;
  depth_to_water_ft: number | null;
  water_elevation_ft: number | null;
  depth_to_product_ft: number | null;
  product_thickness_ft: number | null;
  flag_code: string | null;
}

export interface TimeseriesWell {
  well_id: string;
  well_name: string;
  site_name: string;
  measurements: TimeseriesMeasurement[];
}

export interface GaugingTimeseriesResponse {
  wells: TimeseriesWell[];
}

// ── Phase 3: Risk & Cost Types ───────────────────────────────────────────

export interface RiskPredictionRow {
  site_id: string;
  site_name: string;
  risk_score: number | null;
  risk_category: string | null;
  site_trend: string | null;
  max_exceedance_probability: number | null;
  wells_exceeding_mcl_likely: number | null;
  total_wells_assessed: number | null;
  closure_median_quarters: number | null;
  closure_probability_10yr: number | null;
  closure_confidence: string | null;
  bottleneck_analyte: string | null;
  improving_wells_pct: number | null;
  prediction_date: string | null;
  threat_tier: number | null;
  threat_score: number | null;
  estimated_years_to_closure: number | null;
  estimated_cost_to_closure: number | null;
  probability_enforcement_12mo: number | null;
}

export interface RiskPredictionResponse {
  items: RiskPredictionRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface CostRemediationRow {
  site_id: string;
  site_name: string;
  threat_tier: string | null;
  current_cost: number | null;
  cost_6mo: number | null;
  cost_12mo: number | null;
  cost_24mo: number | null;
  monthly_burn_rate: number | null;
  well_count: number | null;
  computed_at: string | null;
  remediation_technologies: string[];
  total_capital_cost: number | null;
  total_monthly_operating: number | null;
}

export interface CostRemediationResponse {
  items: CostRemediationRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// ── Phase 3: Saved Filter Presets ────────────────────────────────────────

export interface SavedPresetItem {
  id: string;
  name: string;
  filters_json: Record<string, any>;
  is_shared: boolean;
  created_at: string;
}

export interface CreatePresetRequest {
  name: string;
  filters_json: Record<string, any>;
  is_shared: boolean;
}

// ── Regulatory Standards ──────────────────────────────────────────────────

export interface RegulatoryLimitInfo {
  value: number;
  units: string;
  color: string;
  style: 'solid' | 'dashed' | 'dotted';
  label: string;
  library: string;
}

export interface StandardLibrary {
  library: string;
  display_name: string;
  analyte_count: number;
  media: string[];
  description: string;
}

export interface StandardLimit {
  analyte_code: string;
  analyte_name: string | null;
  cas_number: string | null;
  limit_value: number | null;
  limit_units: string;
  calculation_type: string;
  effective_date: string | null;
  source_document: string | null;
  notes: string | null;
}

export interface SiteRegulatoryConfig {
  id?: string;
  standard_library: string;
  display_name: string | null;
  display_color: string;
  display_style: string;
  is_primary: boolean;
  sort_order: number;
  enabled: boolean;
}

export interface SiteSpecificLimit {
  id: string;
  analyte_code: string;
  analyte_name: string | null;
  limit_value: number;
  limit_units: string;
  limit_type: string;
  well_id: string | null;
  well_group: string | null;
  effective_date: string | null;
  superseded_date: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

export interface MultiStandardExceedance {
  result_id: string;
  analyte_code: string;
  compound: string;
  concentration: number;
  exceeds_all: boolean;
  exceeds_some: boolean;
  per_standard: Record<string, { exceeds: boolean; ratio: number; limit_value: number }>;
}

// ── Well Construction Types ──────────────────────────────────────────────

export interface WellElevationHistoryItem {
  id: string;
  elevation_ft: number;
  effective_date: string;
  source: string;
  surveyed_by: string | null;
  notes: string | null;
}

export interface WellConstructionResponse {
  well_id: string;
  well_name: string;
  site_name: string;
  well_type: string | null;
  is_active: boolean;
  casing_elevation_ft: number | null;
  ground_elevation_ft: number | null;
  total_depth_ft: number | null;
  screen_top_ft: number | null;
  screen_bottom_ft: number | null;
  casing_diameter_in: number | null;
  casing_material: string | null;
  specific_gravity: number | null;
  sort_order: number | null;
  well_group: string | null;
  install_date: string | null;
  abandonment_date: string | null;
  status: string | null;
  elevation_history: WellElevationHistoryItem[];
}

// ── Common Filters ───────────────────────────────────────────────────────

export interface DataExplorerFilters {
  site_ids?: string[];
  well_ids?: string[];
  analyte_codes?: string[];
  analyte_groups?: string[];
  date_from?: string;
  date_to?: string;
  exceeds_mcl_only?: boolean;
  nd_handling?: NdHandling;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
  // QA/QC tab: return only QC riders (sample_type != 'N'); sample_types narrows
  // to specific rider types. Default-off keeps the chemistry view unchanged.
  qc_only?: boolean;
  sample_types?: string[];
}

// ── Helpers ──────────────────────────────────────────────────────────────

function filtersToParams(filters: DataExplorerFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.well_ids?.length) params.well_ids = filters.well_ids.join(',');
  if (filters.analyte_codes?.length) params.analyte_codes = filters.analyte_codes.join(',');
  if (filters.analyte_groups?.length) params.analyte_groups = filters.analyte_groups.join(',');
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.exceeds_mcl_only) params.exceeds_mcl_only = 'true';
  if (filters.nd_handling) params.nd_handling = filters.nd_handling;
  if (filters.sort_by) params.sort_by = filters.sort_by;
  if (filters.sort_dir) params.sort_dir = filters.sort_dir;
  if (filters.page) params.page = String(filters.page);
  if (filters.page_size) params.page_size = String(filters.page_size);
  if (filters.qc_only) params.qc_only = 'true';
  if (filters.sample_types?.length) params.sample_types = filters.sample_types.join(',');
  return params;
}

function siteWellDateParams(filters: DataExplorerFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.well_ids?.length) params.well_ids = filters.well_ids.join(',');
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.sort_by) params.sort_by = filters.sort_by;
  if (filters.sort_dir) params.sort_dir = filters.sort_dir;
  if (filters.page) params.page = String(filters.page);
  if (filters.page_size) params.page_size = String(filters.page_size);
  return params;
}

// ── API Methods ──────────────────────────────────────────────────────────

// Phase 1: Filters
export async function fetchFilterSites(product?: string): Promise<FilterSiteItem[]> {
  const resp = await apiClient.get<FilterSiteItem[]>('/data-vault/filters/sites', {
    params: product ? { product } : undefined,
  });
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function fetchFilterWells(siteIds?: string[]): Promise<FilterWellItem[]> {
  const params: Record<string, string> = {};
  if (siteIds?.length) params.site_ids = siteIds.join(',');
  const resp = await apiClient.get<FilterWellItem[]>('/data-vault/filters/wells', { params });
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function fetchFilterAnalytes(siteIds?: string[]): Promise<FilterAnalyteItem[]> {
  const params: Record<string, string> = {};
  if (siteIds?.length) params.site_ids = siteIds.join(',');
  const resp = await apiClient.get<FilterAnalyteItem[]>('/data-vault/filters/analytes', { params });
  return resp.data;
}

// Phase 1: Analytical
export async function fetchAnalyticalResults(filters: DataExplorerFilters): Promise<AnalyticalResponse> {
  const params = filtersToParams(filters);
  const resp = await apiClient.get<AnalyticalResponse>('/data-vault/analytical', { params });
  return resp.data;
}

export function getAnalyticalCsvUrl(filters: DataExplorerFilters): string {
  const params = filtersToParams(filters);
  const qs = new URLSearchParams(params).toString();
  const base = apiClient.defaults.baseURL || '/api/v1';
  return `${base}/data-vault/analytical/export/csv?${qs}`;
}

export async function fetchAnalyticalTimeseries(filters: DataExplorerFilters): Promise<AnalyticalTimeseriesResponse> {
  const params = filtersToParams(filters);
  const resp = await apiClient.get<AnalyticalTimeseriesResponse>('/data-vault/analytical/timeseries', { params });
  return resp.data;
}

// Phase 2: Gauging
export async function fetchGaugingResults(filters: DataExplorerFilters): Promise<GaugingResponse> {
  const params = siteWellDateParams(filters);
  const resp = await apiClient.get<GaugingResponse>('/data-vault/gauging', { params });
  return resp.data;
}

export async function fetchGaugingTimeseries(filters: DataExplorerFilters): Promise<GaugingTimeseriesResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.well_ids?.length) params.well_ids = filters.well_ids.join(',');
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  const resp = await apiClient.get<GaugingTimeseriesResponse>('/data-vault/gauging/timeseries', { params });
  return resp.data;
}

export function getGaugingCsvUrl(filters: DataExplorerFilters): string {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.well_ids?.length) params.well_ids = filters.well_ids.join(',');
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  const qs = new URLSearchParams(params).toString();
  const base = apiClient.defaults.baseURL || '/api/v1';
  return `${base}/data-vault/gauging/export/csv?${qs}`;
}

// Phase 3: Risk & Cost
export async function fetchRiskPredictions(filters: DataExplorerFilters): Promise<RiskPredictionResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.page) params.page = String(filters.page);
  if (filters.page_size) params.page_size = String(filters.page_size);
  const resp = await apiClient.get<RiskPredictionResponse>('/data-vault/risk-predictions', { params });
  return resp.data;
}

// Phreatic predictions — computed on load via the V6 assessment service.
export interface PhreaticPredictionRow {
  site_id: string;
  site_name: string;
  aquifer: string | null;
  years_remaining: number | null;
  decline_ft_yr: number | null;
  avg_sat_thickness_ft: number | null;
  af_available: number | null;
  wells_analyzed: number | null;
  forecast: { year: number; af_remaining: number | null }[] | null;
  error: string | null;
}

export interface PhreaticPredictionResponse {
  items: PhreaticPredictionRow[];
  computed: number;
  truncated: boolean;
}

export async function fetchPhreaticPredictions(filters: DataExplorerFilters): Promise<PhreaticPredictionResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  const resp = await apiClient.get<PhreaticPredictionResponse>('/data-vault/phreatic-predictions', { params });
  return resp.data;
}

// Phreatic per-well water metrics — feeds the phreatic water-level Sankey.
export interface PhreaticWellLevelRow {
  site_name: string;
  well_id: string;
  dtw_ft: number | null;
  gw_elevation_ft: number | null;
  sat_thickness_ft: number | null;
}

export interface PhreaticWellLevelResponse {
  items: PhreaticWellLevelRow[];
  truncated: boolean;
}

export async function fetchPhreaticWellLevels(
  filters: DataExplorerFilters,
  horizonYears = 0,
): Promise<PhreaticWellLevelResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (horizonYears > 0) params.horizon_years = String(horizonYears);
  const resp = await apiClient.get<PhreaticWellLevelResponse>('/data-vault/phreatic-well-levels', { params });
  return resp.data;
}

// Polygon per-well per-analyte predicted concentrations (source of truth:
// well_concentration_predictions). Feeds DV's polygon forecast charts/Sankey.
export interface PolygonWellPredictionRow {
  well_id: string;
  analyte_code: string;
  predicted_conc_mgl: number | null;
  target_date: string | null;
}

export interface PolygonWellPredictionResponse {
  items: PolygonWellPredictionRow[];
}

export async function fetchPolygonWellPredictions(
  filters: DataExplorerFilters,
  horizonYears = 0,
): Promise<PolygonWellPredictionResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (horizonYears > 0) params.horizon_years = String(horizonYears);
  const resp = await apiClient.get<PolygonWellPredictionResponse>('/data-vault/polygon-well-predictions', { params });
  return resp.data;
}

// Phreatic water-level time series (observed + predicted steps), shaped like the
// gauging timeseries so the water-level charts render it.
export async function fetchPhreaticGaugingTimeseries(
  filters: DataExplorerFilters,
  horizonYears = 0,
): Promise<GaugingTimeseriesResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (horizonYears > 0) params.horizon_years = String(horizonYears);
  const resp = await apiClient.get<GaugingTimeseriesResponse>('/data-vault/phreatic-gauging-timeseries', { params });
  return resp.data;
}

export async function fetchCostRemediation(filters: DataExplorerFilters): Promise<CostRemediationResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.page) params.page = String(filters.page);
  if (filters.page_size) params.page_size = String(filters.page_size);
  const resp = await apiClient.get<CostRemediationResponse>('/data-vault/cost-remediation', { params });
  return resp.data;
}

// Phase 3: Excel export URL
export function getXlsxExportUrl(filters: DataExplorerFilters, tabs: string[]): string {
  const params = filtersToParams(filters);
  params.tabs = tabs.join(',');
  const qs = new URLSearchParams(params).toString();
  const base = apiClient.defaults.baseURL || '/api/v1';
  return `${base}/data-vault/export/xlsx?${qs}`;
}

// Phase 3: Saved Presets
export async function fetchSavedPresets(): Promise<SavedPresetItem[]> {
  const resp = await apiClient.get<SavedPresetItem[]>('/data-vault/filters/presets');
  return resp.data;
}

export async function createSavedPreset(body: CreatePresetRequest): Promise<SavedPresetItem> {
  const resp = await apiClient.post<SavedPresetItem>('/data-vault/filters/presets', body);
  return resp.data;
}

export async function deleteSavedPreset(id: string): Promise<void> {
  await apiClient.delete(`/data-vault/filters/presets/${id}`);
}

// ── Phase 4: Plume & Spatial Types ──────────────────────────────────────

export interface PlumeSpatialRow {
  site_id: string;
  site_name: string;
  snapshot_date: string | null;
  plume_area_sqft: number | null;
  plume_length_ft: number | null;
  plume_width_ft: number | null;
  max_concentration_ugl: number | null;
  migration_rate_ft_per_month: number | null;
  wells_within_plume: number | null;
  wells_at_risk_6mo: number | null;
  wells_at_risk_12mo: number | null;
  has_boundary: boolean;
}

export interface PlumeSpatialResponse {
  items: PlumeSpatialRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface GeoJSONFeature {
  type: 'Feature';
  geometry: {
    type: string;
    coordinates: any;
  };
  properties: Record<string, any>;
}

export interface GeoJSONFeatureCollection {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
}

export interface ComplianceRow {
  site_id: string;
  site_name: string;
  total_results: number;
  exceedance_count: number;
  exceedance_pct: number | null;
  analytes_exceeding: string[];
  days_since_last_sample: number | null;
  total_wells: number;
  wells_sampled_last_year: number;
  sampling_completeness_pct: number | null;
  enabled_standards?: string[];
}

export interface ComplianceResponse {
  items: ComplianceRow[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// ── Phase 4: Plume & Compliance API ─────────────────────────────────────

export async function fetchPlumeSpatial(filters: DataExplorerFilters): Promise<PlumeSpatialResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.page) params.page = String(filters.page);
  if (filters.page_size) params.page_size = String(filters.page_size);
  const resp = await apiClient.get<PlumeSpatialResponse>('/data-vault/plume-spatial', { params });
  return resp.data;
}

export async function fetchPlumeGeoJSON(filters: DataExplorerFilters): Promise<GeoJSONFeatureCollection> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  const resp = await apiClient.get<GeoJSONFeatureCollection>('/data-vault/plume-spatial/geojson', { params });
  return resp.data;
}

export async function fetchCompliance(filters: DataExplorerFilters): Promise<ComplianceResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.page) params.page = String(filters.page);
  if (filters.page_size) params.page_size = String(filters.page_size);
  const resp = await apiClient.get<ComplianceResponse>('/data-vault/compliance', { params });
  return resp.data;
}

export function getGeoJSONExportUrl(filters: DataExplorerFilters): string {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  const qs = new URLSearchParams(params).toString();
  const base = apiClient.defaults.baseURL || '/api/v1';
  return `${base}/data-vault/export/geojson?${qs}`;
}

// ── Phase 5: Anomaly & Compare Types ────────────────────────────────────

export interface AnomalyItem {
  result_id: string;
  z_score: number;
  direction: 'high' | 'low';
}

export interface AnomaliesResponse {
  anomalies: AnomalyItem[];
  threshold: number;
  total_flagged: number;
}

export interface CompareGroupStats {
  label: string;
  sample_count: number;
  avg_concentration: number | null;
  max_concentration: number | null;
  min_concentration: number | null;
  exceedance_count: number;
  non_detect_count: number;
}

export interface CompareAnalyteRow {
  analyte_code: string;
  compound: string;
  group_a: CompareGroupStats;
  group_b: CompareGroupStats;
  pct_change: number | null;
}

export interface CompareResponse {
  mode: 'sites' | 'periods';
  label_a: string;
  label_b: string;
  analytes: CompareAnalyteRow[];
}

// ── Phase 5: Anomaly & Compare API ──────────────────────────────────────

export async function fetchAnomalies(
  filters: DataExplorerFilters,
  threshold: number = 2.0,
): Promise<AnomaliesResponse> {
  const params: Record<string, string> = {};
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.well_ids?.length) params.well_ids = filters.well_ids.join(',');
  if (filters.analyte_codes?.length) params.analyte_codes = filters.analyte_codes.join(',');
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  params.threshold = String(threshold);
  const resp = await apiClient.get<AnomaliesResponse>('/data-vault/analytical/anomalies', { params });
  return resp.data;
}

export interface CompareFilters {
  mode: 'sites' | 'periods';
  site_ids_a?: string[];
  site_ids_b?: string[];
  site_ids?: string[];
  period_a_from?: string;
  period_a_to?: string;
  period_b_from?: string;
  period_b_to?: string;
  analyte_codes?: string[];
  well_ids?: string[];
}

// ── Phase 6: Audit Log Types ─────────────────────────────────────────────

export interface AuditLogItem {
  id: string;
  export_type: string;
  tabs_included: string | null;
  filters_json: Record<string, any> | null;
  row_count: number | null;
  file_size_bytes: number | null;
  user_email: string | null;
  created_at: string;
}

export interface AuditLogResponse {
  items: AuditLogItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// ── Phase 6: Audit Log & Export API ──────────────────────────────────────

export async function fetchAuditLog(page: number = 1, pageSize: number = 25): Promise<AuditLogResponse> {
  const resp = await apiClient.get<AuditLogResponse>('/data-vault/audit/exports', {
    params: { page: String(page), page_size: String(pageSize) },
  });
  return resp.data;
}

export function getBulkZipUrl(filters: DataExplorerFilters, tabs: string[]): string {
  const params = filtersToParams(filters);
  params.tabs = tabs.join(',');
  const qs = new URLSearchParams(params).toString();
  const base = apiClient.defaults.baseURL || '/api/v1';
  return `${base}/data-vault/export/bulk-zip?${qs}`;
}

export async function fetchCompare(filters: CompareFilters): Promise<CompareResponse> {
  const params: Record<string, string> = { mode: filters.mode };
  if (filters.site_ids_a?.length) params.site_ids_a = filters.site_ids_a.join(',');
  if (filters.site_ids_b?.length) params.site_ids_b = filters.site_ids_b.join(',');
  if (filters.site_ids?.length) params.site_ids = filters.site_ids.join(',');
  if (filters.period_a_from) params.period_a_from = filters.period_a_from;
  if (filters.period_a_to) params.period_a_to = filters.period_a_to;
  if (filters.period_b_from) params.period_b_from = filters.period_b_from;
  if (filters.period_b_to) params.period_b_to = filters.period_b_to;
  if (filters.analyte_codes?.length) params.analyte_codes = filters.analyte_codes.join(',');
  if (filters.well_ids?.length) params.well_ids = filters.well_ids.join(',');
  const resp = await apiClient.get<CompareResponse>('/data-vault/analytical/compare', { params });
  return resp.data;
}

// ── Well Construction API ────────────────────────────────────────────────

export async function fetchWellConstruction(wellId: string): Promise<WellConstructionResponse> {
  const resp = await apiClient.get<WellConstructionResponse>(`/data-vault/wells/${wellId}/construction`);
  return resp.data;
}

/** Editable well-construction fields. Send any subset; only the keys present
 *  are applied server-side. */
export interface WellConstructionUpdate {
  well_type?: string | null;
  casing_elevation_ft?: number | null;
  ground_elevation_ft?: number | null;
  total_depth_ft?: number | null;
  screen_top_ft?: number | null;
  screen_bottom_ft?: number | null;
  casing_diameter_in?: number | null;
  casing_material?: string | null;
  install_date?: string | null;
  abandonment_date?: string | null;
  is_active?: boolean | null;
  specific_gravity?: number | null;
  sort_order?: number | null;
  well_group?: string | null;
}

/** Update well construction fields (any subset). */
export async function updateWellConstruction(
  wellId: string,
  body: WellConstructionUpdate,
): Promise<WellConstructionResponse> {
  const resp = await apiClient.put<WellConstructionResponse>(
    `/data-vault/wells/${wellId}/construction`,
    body,
  );
  return resp.data;
}

export interface AddElevationHistoryBody {
  /** New top-of-casing elevation, ft MSL. */
  elevation_ft: number;
  /** ISO date (YYYY-MM-DD) the new elevation takes effect. */
  effective_date: string;
  /** 'survey' | 'import' | 'manual' — defaults to 'survey'. */
  source?: string;
  surveyed_by?: string | null;
  notes?: string | null;
}

/** Record a re-survey: a new TOC elevation effective from a given date.
 *  Gaugings before that date keep their old elevation; the well's current
 *  casing_elevation_ft is updated if this is the latest epoch. */
export async function addWellElevationHistory(
  wellId: string,
  body: AddElevationHistoryBody,
): Promise<WellElevationHistoryItem> {
  const resp = await apiClient.post<WellElevationHistoryItem>(
    `/data-vault/wells/${wellId}/elevation-history`,
    body,
  );
  return resp.data;
}

// ── Regulatory Standards API ──────────────────────────────────────────────

export async function fetchRegulatoryStandards(): Promise<StandardLibrary[]> {
  const resp = await apiClient.get<StandardLibrary[]>('/data-vault/regulatory/standards');
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function fetchStandardLimits(
  library: string,
  page: number = 1,
  pageSize: number = 100,
): Promise<{ items: StandardLimit[]; total: number }> {
  const resp = await apiClient.get(`/data-vault/regulatory/standards/${encodeURIComponent(library)}`, {
    params: { page, page_size: pageSize },
  });
  return resp.data;
}

export async function fetchSiteRegulatoryConfig(siteId: string): Promise<SiteRegulatoryConfig[]> {
  const resp = await apiClient.get<SiteRegulatoryConfig[]>(`/data-vault/sites/${siteId}/regulatory/config`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function updateSiteRegulatoryConfig(
  siteId: string,
  configs: SiteRegulatoryConfig[],
): Promise<SiteRegulatoryConfig[]> {
  const resp = await apiClient.put<SiteRegulatoryConfig[]>(`/data-vault/sites/${siteId}/regulatory/config`, { configs });
  return resp.data;
}

export interface SiteExceedanceRule {
  site_id: string;
  exceedance_rule: ExceedanceRule;
}

export async function fetchSiteExceedanceRule(siteId: string): Promise<SiteExceedanceRule> {
  const resp = await apiClient.get<SiteExceedanceRule>(`/data-vault/sites/${siteId}/exceedance-rule`);
  return resp.data;
}

export async function updateSiteExceedanceRule(
  siteId: string,
  rule: ExceedanceRule,
): Promise<SiteExceedanceRule> {
  const resp = await apiClient.patch<SiteExceedanceRule>(
    `/data-vault/sites/${siteId}/exceedance-rule`,
    { exceedance_rule: rule },
  );
  return resp.data;
}

// ── QA Review Queue ────────────────────────────────────────────────────

export interface QaQueueItem {
  id: string;
  site_id: string;
  site_name: string;
  well_id: string | null;
  well_name: string | null;
  sample_date: string;
  compound: string;
  analyte_code: string | null;
  concentration: number | null;
  units: string | null;
  unit_inferred_from: string | null;
  qa_flag: string;
  qa_flag_detail: {
    expected_median?: number;
    observed?: number;
    ratio?: number;
    n_samples?: number;
    units_raw?: string;
    reason?: string;
  } | null;
  qa_review_action: 'confirmed' | 'corrected' | 'dismissed' | null;
  qa_reviewed_at: string | null;
}

export interface QaQueueResponse {
  items: QaQueueItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export async function fetchQaReviewQueue(params: {
  site_ids?: string[];
  status?: 'pending' | 'all';
  page?: number;
  page_size?: number;
} = {}): Promise<QaQueueResponse> {
  const query: Record<string, string> = {};
  if (params.site_ids?.length) query.site_ids = params.site_ids.join(',');
  if (params.status) query.status = params.status;
  if (params.page) query.page = String(params.page);
  if (params.page_size) query.page_size = String(params.page_size);
  const resp = await apiClient.get<QaQueueResponse>('/data-vault/qa-review/queue', { params: query });
  return resp.data;
}

export async function confirmQaItem(resultId: string): Promise<QaQueueItem> {
  const resp = await apiClient.post<QaQueueItem>(`/data-vault/qa-review/${resultId}/confirm`);
  return resp.data;
}

export async function dismissQaItem(resultId: string): Promise<QaQueueItem> {
  const resp = await apiClient.post<QaQueueItem>(`/data-vault/qa-review/${resultId}/dismiss`);
  return resp.data;
}

export async function correctQaItem(
  resultId: string,
  body: { units?: string; concentration?: number },
): Promise<QaQueueItem> {
  const resp = await apiClient.post<QaQueueItem>(`/data-vault/qa-review/${resultId}/correct`, body);
  return resp.data;
}

// ── Self-healing intake agent ───────────────────────────────────────────────
export interface HealProposal {
  id: string;
  row_id: string | null;
  row_number: number | null;
  rule: string | null;
  field: string | null;
  remediation_type: string;
  label: string;
  tier: 'data' | 'knowledge';
  confidence: number | null;
  rationale: string | null;
  before_value: string | null;
  after_value: string | null;
  status: 'open' | 'accepted' | 'rejected' | 'auto_applied' | 'superseded';
  source: 'llm' | 'cache' | 'deterministic';
  learned_rule_ref: Record<string, unknown> | null;
  created_at: string | null;
}

export interface HealProposalList {
  items: HealProposal[];
  open_count: number;
  total: number;
}

export interface HealRunResult {
  created: number;
  diagnosed: number;
  cached: number;
  capped: boolean;
  skipped_covered: number;
  by_type: Record<string, number>;
}

export interface ReconcileResult {
  findings: Array<Record<string, unknown>>;
  checked_groups: number;
  note?: string;
}

export async function runHeal(batchId: string, maxDiagnoses = 40): Promise<HealRunResult> {
  const resp = await apiClient.post<HealRunResult>(
    `/data-vault/intake/batches/${batchId}/heal`, undefined,
    { params: { max_diagnoses: maxDiagnoses } },
  );
  return resp.data;
}

export async function fetchHealProposals(
  batchId: string, status: 'open' | 'all' = 'all',
): Promise<HealProposalList> {
  const resp = await apiClient.get<HealProposalList>(
    `/data-vault/intake/batches/${batchId}/heal-proposals`, { params: { status } },
  );
  return resp.data;
}

export async function acceptHealProposal(
  batchId: string, proposalId: string, note?: string,
): Promise<{ status: string; learned: Record<string, unknown> | null; validation: Record<string, number> }> {
  const resp = await apiClient.post(
    `/data-vault/intake/batches/${batchId}/heal-proposals/${proposalId}/accept`, { note },
  );
  return resp.data;
}

export async function rejectHealProposal(
  batchId: string, proposalId: string, note?: string,
): Promise<{ status: string }> {
  const resp = await apiClient.post(
    `/data-vault/intake/batches/${batchId}/heal-proposals/${proposalId}/reject`, { note },
  );
  return resp.data;
}

export async function acceptAllHealProposals(
  batchId: string, includeKnowledge = false,
): Promise<{ status: string; accepted: number; failed: number; validation: Record<string, number> }> {
  const resp = await apiClient.post(
    `/data-vault/intake/batches/${batchId}/heal-proposals/accept-all`, undefined,
    { params: { include_knowledge: includeKnowledge } },
  );
  return resp.data;
}

export async function rejectAllHealProposals(
  batchId: string,
): Promise<{ status: string; rejected: number }> {
  const resp = await apiClient.post(
    `/data-vault/intake/batches/${batchId}/heal-proposals/reject-all`,
  );
  return resp.data;
}

export async function reconcileBatch(batchId: string): Promise<ReconcileResult> {
  const resp = await apiClient.post<ReconcileResult>(`/data-vault/intake/batches/${batchId}/reconcile`);
  return resp.data;
}

export async function fetchSiteSpecificLimits(siteId: string): Promise<SiteSpecificLimit[]> {
  const resp = await apiClient.get<SiteSpecificLimit[]>(`/data-vault/sites/${siteId}/regulatory/limits`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function createSiteSpecificLimit(
  siteId: string,
  limit: {
    analyte_code: string;
    analyte_name?: string;
    limit_value: number;
    limit_units?: string;
    limit_type: string;
    well_id?: string;
    well_group?: string;
    effective_date?: string;
    notes?: string;
  },
): Promise<SiteSpecificLimit> {
  const resp = await apiClient.post<SiteSpecificLimit>(`/data-vault/sites/${siteId}/regulatory/limits`, limit);
  return resp.data;
}

export async function deleteSiteSpecificLimit(siteId: string, limitId: string): Promise<void> {
  await apiClient.delete(`/data-vault/sites/${siteId}/regulatory/limits/${limitId}`);
}

export async function fetchMultiStandardExceedances(
  siteId: string,
  filters?: {
    analyte_codes?: string[];
    well_ids?: string[];
    date_from?: string;
    date_to?: string;
  },
): Promise<MultiStandardExceedance[]> {
  const params: Record<string, string> = {};
  if (filters?.analyte_codes?.length) params.analyte_codes = filters.analyte_codes.join(',');
  if (filters?.well_ids?.length) params.well_ids = filters.well_ids.join(',');
  if (filters?.date_from) params.date_from = filters.date_from;
  if (filters?.date_to) params.date_to = filters.date_to;
  const resp = await apiClient.get<MultiStandardExceedance[]>(`/data-vault/sites/${siteId}/exceedances`, { params });
  return resp.data;
}
